import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Admin } from './models/admin';
import { ApiUtils } from './utils/api';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

interface AuthStoreState {
  isLoggedIn: boolean;
  admin: Admin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ message: string }>;

  _error: any;
  _setError: (error: any) => void;
  _hasHydrated: boolean;
  _setHasHydrated: (state: boolean) => void;
}

export const useAuth = create<AuthStoreState>()(
  persist(
    (set, get) => ({
      isLoggedIn: false,
      admin: null,
      token: null,

      login: async (email: string, password: string) => {
        const response = await ApiUtils.post<{
          accessToken: string;
          admin: Admin;
        }>('auth/login', { email, password });

        set({
          isLoggedIn: true,
          token: response.accessToken,
          admin: response.admin,
        });
      },

      logout: async () => {
        try {
          await ApiUtils.post('auth/logout', {}, { skipErrorCheckUnauthorised: true });
        } catch (error) {
          console.error(error);
        }
        set({
          isLoggedIn: false,
          token: null,
          admin: null,
        });
      },

      fetchUser: async () => {
        const token = get().token;
        if (token) {
          const response = await ApiUtils.get<Admin>('me');
          set({
            isLoggedIn: true,
            admin: response,
          });
        } else {
          set({
            isLoggedIn: false,
            admin: null,
          });
        }
      },

      requestPasswordReset: async (email: string) => {
        return await ApiUtils.post<{ message: string }>('auth/request-reset', { email });
      },

      resetPassword: async (token: string, newPassword: string) => {
        return await ApiUtils.post<{ message: string }>('auth/reset-password', { token, newPassword });
      },

      _error: null,
      _setError: (error) => {
        set({
          _error: error,
        });
      },
      _hasHydrated: false,
      _setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
    }),
    {
      name: 'auth',
      partialize: (state) => ({
        token: state.token,
      }),
      onRehydrateStorage: (state) => {
        return (state, error) => {
          setTimeout(() => {
            if (error) state?._setHasHydrated(true);
            if (state?.token && !state?.isLoggedIn) {
              state
                ?.fetchUser()
                .catch((error) => {
                  // If token is expired/invalid, clear auth state instead of showing error
                  // handleApiError in api.ts already calls logout() for 401/403
                  console.error('Failed to fetch user on rehydration:', error);
                })
                .finally(() => {
                  state?._setHasHydrated(true);
                });
            } else {
              state?._setHasHydrated(true);
            }
          });
        };
      },
    }
  )
);
